import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Table, Input, Label, Button, Row, Col } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import Header from "../../layout/Header/Header";
import { hoyLocal } from "../../fechaLocal";
import { exportaExcelConFotoOpcional } from "../../excelConFotos";

// Lo que falta por recibir, por modelo y con sus fechas. Junta dos etapas:
//   En transito: ya viene en un embarque; su fecha es la ETA real.
//   Por embarcar: sigue en una PO abierta; su fecha es la compromiso (estimada).
// "Recibo parcial" es lo que falto en un embarque ya recibido: no viene en camino,
// por eso va aparte. Con la existencia y lo que falta surtir se ve si va a alcanzar.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const COLOR_ETAPA = { "En transito": "#0ea5e9", "Por embarcar": "#6366f1", "Recibo parcial": "#b45309" };

const diasPara = (fecha, hoy) => (fecha ? moment(fecha).startOf("day").diff(moment(hoy).startOf("day"), "days") : null);

function Fecha({ fecha, estimada, hoy }) {
  if (!fecha) return <span style={{ color: "#94a3b8" }}>Sin fecha</span>;
  const dias = diasPara(fecha, hoy);
  const color = dias < 0 ? "#dc2626" : dias <= 15 ? "#b45309" : "#475569";
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <b style={{ color }}>{fecha}</b>
      <div style={{ fontSize: 11, color }}>
        {estimada ? "compromiso · " : "ETA · "}
        {dias < 0 ? `venció hace ${-dias} d` : dias === 0 ? "hoy" : `en ${dias} d`}
      </div>
    </span>
  );
}

function Dato({ titulo, valor, color }) {
  return (
    <div style={{ flex: "1 1 150px", padding: "10px 14px", border: "1px solid #e2e8f0", borderRadius: 8 }}>
      <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#0f172a" }}>{valor}</div>
    </div>
  );
}

function PendienteRecibir() {
  const { user } = useContext(AuthContext);
  const puede = !!user.menu_ventas || !!user.menu_compras || !!user.inventarios;
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [etapa, setEtapa] = useState("");
  const [cuando, setCuando] = useState("");
  const [soloFaltantes, setSoloFaltantes] = useState(false);
  const [abiertos, setAbiertos] = useState({});
  const hoy = hoyLocal();

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_URL_SEGUIMIENTOS}PendienteRecibir`, { headers: headers() })
      .then((r) => { setFilas(r.data || []); setCargando(false); })
      .catch((e) => {
        setCargando(false);
        Swal.fire({ icon: "error", title: "No se pudo cargar el reporte", text: e.response?.data?.message || e.message });
      });
  }, []);

  const proveedores = useMemo(() => [...new Set(filas.flatMap((f) => f.detalles.map((d) => d.proveedor)).filter(Boolean))].sort(), [filas]);

  // Los filtros van sobre el detalle (embarque o PO); el modelo suma lo que queda
  const visibles = useMemo(() => {
    const q = buscar.trim().toUpperCase();
    const pasaFecha = (d) => {
      if (!cuando) return true;
      const dias = diasPara(d.fecha, hoy);
      if (cuando === "sinFecha") return dias === null;
      if (dias === null) return false;
      if (cuando === "vencidas") return dias < 0;
      return dias >= 0 && dias <= Number(cuando);
    };
    return filas.map((f) => {
      const detalles = f.detalles.filter((d) => (!proveedor || d.proveedor === proveedor) && (!etapa || d.etapa === etapa) && pasaFecha(d));
      const suma = (e) => detalles.filter((d) => d.etapa === e).reduce((s, d) => s + d.piezas, 0);
      const transito = suma("En transito"), porEmbarcar = suma("Por embarcar"), parcial = suma("Recibo parcial");
      const total = transito + porEmbarcar + parcial;
      const balance = f.existencia + total - f.porSurtir;
      const proxima = detalles.find((d) => d.fecha && d.etapa !== "Recibo parcial") || detalles[0];
      return { ...f, detalles, transito, porEmbarcar, parcial, total, balance, proxima };
    }).filter((f) => f.detalles.length
      && (!soloFaltantes || f.balance < 0)
      && (!q || [f.codigo, f.nombre, f.marca, ...f.detalles.flatMap((d) => [d.documento, d.pos])].some((x) => String(x || "").toUpperCase().includes(q))));
  }, [filas, buscar, proveedor, etapa, cuando, soloFaltantes, hoy]);

  const detalles = visibles.flatMap((f) => f.detalles);
  const piezas = (fn) => detalles.filter(fn).reduce((s, d) => s + d.piezas, 0);
  const porMes = useMemo(() => {
    const m = {};
    visibles.flatMap((f) => f.detalles).filter((d) => d.fecha && d.etapa !== "Recibo parcial").forEach((d) => {
      const k = d.fecha.slice(0, 7);
      m[k] = (m[k] || 0) + d.piezas;
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [visibles]);

  const excel = () => exportaExcelConFotoOpcional({
    filas: visibles.flatMap((f) => f.detalles.map((d) => ({
      _idArticulo: f.idArticulo, Modelo: f.codigo, Nombre: f.nombre, Marca: f.marca, Etapa: d.etapa,
      Documento: d.documento, PO: d.pos, Proveedor: d.proveedor,
      "Tipo de fecha": d.estimada ? "Compromiso" : "ETA", Fecha: d.fecha, Dias: diasPara(d.fecha, hoy) ?? "",
      Colores: d.colores, Tallas: d.tallas, Piezas: d.piezas,
      Existencia: f.existencia, "Por surtir": f.porSurtir,
    }))),
    columnas: ["Modelo", "Nombre", "Marca", "Etapa", "Documento", "PO", "Proveedor", "Tipo de fecha", "Fecha", "Dias", "Colores", "Tallas", "Piezas", "Existencia", "Por surtir"],
    nombreArchivo: "PendienteRecibir",
    hoja: "Pendiente recibir",
  });

  const limpiar = () => { setBuscar(""); setProveedor(""); setEtapa(""); setCuando(""); setSoloFaltantes(false); };

  return (
    <>
      <Header />
      <br />
      <br />
      {puede ? (
        <div className="card container col-12">
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Button href="/MenuOperacion" size="sm" className="btn btn-danger">Regresar</Button>
            <Button size="sm" color="primary" onClick={excel} disabled={!visibles.length}>Excel</Button>
            <Button size="sm" color="secondary" onClick={limpiar}>Limpiar filtros</Button>
          </div>
          <h3 align="center" style={{ marginTop: 6 }}>Pendiente de recibir</h3>
          <p align="center" style={{ color: "#64748b", marginBottom: 12 }}>
            Por modelo, con la ETA de lo que ya viene en embarque y la fecha compromiso de lo que falta embarcar.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <Dato titulo="Por recibir" valor={num(piezas(() => true))} />
            <Dato titulo="En tránsito" valor={num(piezas((d) => d.etapa === "En transito"))} color={COLOR_ETAPA["En transito"]} />
            <Dato titulo="Por embarcar" valor={num(piezas((d) => d.etapa === "Por embarcar"))} color={COLOR_ETAPA["Por embarcar"]} />
            <Dato titulo="Recibo parcial" valor={num(piezas((d) => d.etapa === "Recibo parcial"))} color={COLOR_ETAPA["Recibo parcial"]} />
            <Dato titulo="Con fecha vencida" valor={num(piezas((d) => d.etapa !== "Recibo parcial" && d.fecha && d.fecha < hoy))} color="#dc2626" />
            <Dato titulo="Modelos que no alcanzan" valor={num(visibles.filter((f) => f.balance < 0).length)} color="#dc2626" />
          </div>

          {porMes.length ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 12, fontSize: 13 }}>
              <span style={{ color: "#64748b" }}>Por mes:</span>
              {porMes.map(([mes, q]) => (
                <span key={mes} style={{ padding: "2px 10px", borderRadius: 12, background: "#f1f5f9", whiteSpace: "nowrap" }}>
                  {MESES[Number(mes.slice(5, 7)) - 1]} {mes.slice(0, 4)}: <b>{num(q)}</b>
                </span>
              ))}
            </div>
          ) : null}

          <Row style={{ marginBottom: 10 }}>
            <Col md={3}>
              <Label>Buscar</Label>
              <Input bsSize="sm" placeholder="Modelo, marca, contenedor o PO" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
            </Col>
            <Col md={3}>
              <Label>Proveedor</Label>
              <Input bsSize="sm" type="select" value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
                <option value="">Todos</option>
                {proveedores.map((p) => <option key={p} value={p}>{p}</option>)}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Etapa</Label>
              <Input bsSize="sm" type="select" value={etapa} onChange={(e) => setEtapa(e.target.value)}>
                <option value="">Todas</option>
                <option value="En transito">En tránsito</option>
                <option value="Por embarcar">Por embarcar</option>
                <option value="Recibo parcial">Recibo parcial</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label>Fecha</Label>
              <Input bsSize="sm" type="select" value={cuando} onChange={(e) => setCuando(e.target.value)}>
                <option value="">Todas</option>
                <option value="vencidas">Vencidas</option>
                <option value="15">Próximos 15 días</option>
                <option value="30">Próximos 30 días</option>
                <option value="60">Próximos 60 días</option>
                <option value="sinFecha">Sin fecha</option>
              </Input>
            </Col>
            <Col md={2} style={{ display: "flex", alignItems: "flex-end" }}>
              <Label style={{ fontSize: 12, marginBottom: 4, textTransform: "none", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={soloFaltantes} onChange={(e) => setSoloFaltantes(e.target.checked)} /> No alcanzan
              </Label>
            </Col>
          </Row>

          <div style={{ overflowX: "auto" }}>
            <Table size="sm" borderless className="table-responsive-xl">
              <thead>
                <tr>
                  <th className="tituloTabla" style={{ width: 28 }}></th>
                  <th className="tituloTabla">Foto</th>
                  <th className="tituloTabla">Modelo</th>
                  <th className="tituloTabla">Proveedor</th>
                  <th className="tituloTabla">Próxima fecha</th>
                  <th className="tituloTabla" style={{ textAlign: "right" }}>En tránsito</th>
                  <th className="tituloTabla" style={{ textAlign: "right" }}>Por embarcar</th>
                  <th className="tituloTabla" style={{ textAlign: "right" }}>Por recibir</th>
                  <th className="tituloTabla" style={{ textAlign: "right" }}>Existencia</th>
                  <th className="tituloTabla" style={{ textAlign: "right" }}>Por surtir</th>
                  <th className="tituloTabla" style={{ textAlign: "right" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {cargando ? (
                  <tr><td colSpan={11} style={{ textAlign: "center", color: "#94a3b8" }}>Cargando...</td></tr>
                ) : !visibles.length ? (
                  <tr><td colSpan={11} style={{ textAlign: "center", color: "#94a3b8" }}>No hay piezas pendientes con esos filtros.</td></tr>
                ) : visibles.map((f) => (
                  <React.Fragment key={f.idArticulo}>
                    <tr style={{ borderTop: "1px solid #e2e8f0" }}>
                      <td style={{ verticalAlign: "middle" }}>
                        <Button color="link" size="sm" style={{ padding: 0 }} title="Ver embarques y PO"
                          onClick={() => setAbiertos((p) => ({ ...p, [f.idArticulo]: !p[f.idArticulo] }))}>
                          <i className={`fas fa-chevron-${abiertos[f.idArticulo] ? "down" : "right"}`}></i>
                        </Button>
                      </td>
                      <td>
                        <img alt="" src={f.urlFoto || `${process.env.PUBLIC_URL}/sinImagen.png`}
                          style={{ width: 42, height: 42, objectFit: "cover", borderRadius: 6, border: "1px solid #e2e8f0" }} />
                      </td>
                      <td style={{ verticalAlign: "middle" }}>
                        <b>{f.codigo}</b> {f.nombre}
                        {f.marca ? <div style={{ fontSize: 11, color: "#64748b" }}>{f.marca}</div> : null}
                      </td>
                      <td style={{ verticalAlign: "middle", fontSize: 13 }}>{[...new Set(f.detalles.map((d) => d.proveedor).filter(Boolean))].join(", ")}</td>
                      <td style={{ verticalAlign: "middle" }}><Fecha fecha={f.proxima?.fecha} estimada={f.proxima?.estimada} hoy={hoy} /></td>
                      <td style={{ textAlign: "right", verticalAlign: "middle" }}>{num(f.transito)}</td>
                      <td style={{ textAlign: "right", verticalAlign: "middle" }}>{num(f.porEmbarcar)}</td>
                      <td style={{ textAlign: "right", verticalAlign: "middle", fontWeight: 700 }}>{num(f.total)}</td>
                      <td style={{ textAlign: "right", verticalAlign: "middle" }}>{num(f.existencia)}</td>
                      <td style={{ textAlign: "right", verticalAlign: "middle" }}>{num(f.porSurtir)}</td>
                      <td style={{ textAlign: "right", verticalAlign: "middle", fontWeight: 700, color: f.balance < 0 ? "#dc2626" : "#15803d" }}>{num(f.balance)}</td>
                    </tr>
                    {abiertos[f.idArticulo] ? (
                      <tr>
                        <td></td>
                        <td colSpan={10} style={{ paddingBottom: 12 }}>
                          <Table size="sm" bordered style={{ marginBottom: 0, background: "#f8fafc" }}>
                            <thead>
                              <tr>
                                <th>Etapa</th><th>Contenedor / PO</th><th>PO</th><th>Proveedor</th><th>Fecha</th>
                                <th>Colores</th><th>Tallas</th><th style={{ textAlign: "right" }}>Piezas</th>
                              </tr>
                            </thead>
                            <tbody>
                              {f.detalles.map((d) => (
                                <tr key={d.key}>
                                  <td style={{ color: COLOR_ETAPA[d.etapa], fontWeight: 600, whiteSpace: "nowrap" }}>{d.etapa === "En transito" ? "En tránsito" : d.etapa}</td>
                                  <td>{d.documento}</td>
                                  <td>{d.pos}</td>
                                  <td>{d.proveedor}</td>
                                  <td><Fecha fecha={d.fecha} estimada={d.estimada} hoy={hoy} /></td>
                                  <td style={{ fontSize: 12 }}>{d.colores}</td>
                                  <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{d.tallas}</td>
                                  <td style={{ textAlign: "right" }}>{num(d.piezas)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
          <p style={{ color: "#64748b", fontSize: 12 }}>
            Balance = existencia + por recibir − por surtir. En rojo, lo que hoy no alcanzaría para los pedidos abiertos.
          </p>
        </div>
      ) : undefined}
    </>
  );
}

export default PendienteRecibir;
